import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    FormControlLabel,
    Radio,
    RadioGroup,
    TextareaAutosize,
    Typography
} from '@mui/material';
import React, { Dispatch, useState, useEffect } from 'react';
import { PhysicianReviewModel } from '../../lib/models/PhysicianReviewModel';
import DecisionReasonSelectMedicalDetermination from '../CaseDetails/DecisionReasonSelectMedicalDetermination';
import t from '../../theme';
import {
    headerRowStyle,
    radioRowStyle,
    medicalDeterminationRationaleRowStyle,
    medicalDeterminationRationaleTextStyle
} from '../../style';
import { useScreenSmallerThan } from '../../lib/hooks/breakpoint-hooks';
import { CaseStages } from '../../lib/constants/misc';
import { ClinicalReviewActivityModel } from '../../lib/models/ClinicalReviewActivityModel';
import { mdNurseDeterminationComparer } from '../../lib/helper/md-nurse-determination-comparer';
import { post } from '../../lib/api';
import { useGet } from '../../lib/api/hooks';

export const MedicalReviewDeterminationConst = {
    Approved: 'Approved',
    PartiallyApproved: 'Partially Approved',
    Denied: 'Denied'
};

export interface Reason {
    value: number;
    text: string;
}

interface PhysicianReviewProps {
    physicianReview: PhysicianReviewModel | undefined;
    clinicalReviews: ClinicalReviewActivityModel[];
    clinicalReviewId: string;
    label: string;
    caseStage: string;
    setAnyChangeMade: Dispatch<boolean>;
    setHasDeterminationDisagreement: Dispatch<boolean>;
    handleOpenDisagreementQuestionsModal: () => void;
}

const PhysicianReviewSecondPart = ({
    physicianReview,
    caseStage,
    clinicalReviews,
    clinicalReviewId,
    setAnyChangeMade,
    setHasDeterminationDisagreement,
    handleOpenDisagreementQuestionsModal
}: PhysicianReviewProps) => {
    const [selectedReason, setSelectedReason] = useState<Reason['value'] | null>(
        physicianReview?.appP2PReasonValue ?? null
    );

    const { data: appReasonsList } = useGet<Reason[]>('casedetails/appp2preasons');

    const [reasonError, setReasonError] = useState(false);

    const isSmallerThanSmall = useScreenSmallerThan('sm');
    const [medicalReviewDeterminationValue, setMedicalReviewDeterminationValue] = useState(
        physicianReview?.medicalReviewDetermination ?? ''
    );

    const [reasonMedicalReviewDeterminationValue, setReasonMedicalReviewDeterminationValue] =
        useState(physicianReview?.reasonMedicalReviewDetermination ?? '');

    const [determinationAlertOpen, setDeterminationAlertOpen] = useState(false);

    const [isAppAppropriate, setIsAppAppropriate] = useState<boolean | null>(
        physicianReview?.isAppAppropriate ?? null
    );

    /* eslint-disable @typescript-eslint/no-explicit-any */
    const handleReasonMedicalReviewDetermination = (event: any) => {
        setAnyChangeMade(true);
        if (event.nativeEvent.inputType === 'insertLineBreak') {
            setReasonMedicalReviewDeterminationValue(reasonMedicalReviewDeterminationValue);
        } else {
            setReasonMedicalReviewDeterminationValue(event.target.value);
        }
    };

    const onMedicalDeterminationChange = async (mdDetermination: string) => {
        if (caseStage === CaseStages.Initial) {
            const initialReview = clinicalReviews
                .filter((cr) => cr.activityType === 'Initial')
                .at(-1);
            const doesMDNurseDeterminationAlign = mdNurseDeterminationComparer(
                initialReview?.nurseDecision,
                mdDetermination
            );

            setHasDeterminationDisagreement(!doesMDNurseDeterminationAlign);

            if (!doesMDNurseDeterminationAlign) {
                post('casedetails/savedeterminationalert', {
                    clinicalReviewId,
                    nurseDetermination: initialReview?.nurseDecision,
                    doctorDetermination: mdDetermination
                });
                setDeterminationAlertOpen(true);
            } else {
                await post('determinationdisagreement/delete', {
                    clinicalReviewId
                });
            }

            if (mdDetermination === MedicalReviewDeterminationConst.Approved) {
                setIsAppAppropriate(null);
            }
        }
    };

    const handleAcknowledged = () => {
        setDeterminationAlertOpen(false);
        handleOpenDisagreementQuestionsModal();
    };

    useEffect(() => {
        if (!physicianReview) return;

        physicianReview.medicalReviewDetermination = medicalReviewDeterminationValue;
        physicianReview.reasonMedicalReviewDetermination = reasonMedicalReviewDeterminationValue;
        physicianReview.isAppAppropriate = isAppAppropriate;

        if (selectedReason != null) {
            physicianReview.appP2PReasonValue = selectedReason;
            physicianReview.appP2PReasonText =
                appReasonsList?.find((r) => r.value === selectedReason)?.text ?? null;
        } else {
            physicianReview.appP2PReasonValue = null;
            physicianReview.appP2PReasonText = null;
        }
    }, [
        medicalReviewDeterminationValue,
        reasonMedicalReviewDeterminationValue,
        isAppAppropriate,
        appReasonsList,
        selectedReason,
        physicianReview
    ]);

    return (
        <Box
            sx={{
                margin: !isSmallerThanSmall ? '0 1.25rem 0.875rem' : '0',
                padding: !isSmallerThanSmall ? '0 0.625rem 0.625rem' : '0.625rem 0.625rem',
                background: 'white',
                borderRadius: !isSmallerThanSmall ? '0.625rem' : '0'
            }}>
            <Box sx={isSmallerThanSmall ? { padding: '0' } : { paddingTop: '0.625rem' }}>
                <Box sx={headerRowStyle}>
                    <Typography variant="h5">Medical Determination Rationale</Typography>
                </Box>
                {caseStage === CaseStages.Initial ? (
                    <Box
                        sx={
                            !isSmallerThanSmall
                                ? radioRowStyle
                                : {
                                      padding: 0,
                                      borderStyle: 'solid',
                                      borderWidth: '0 0.063rem',
                                      borderColor: t.palette.globalSwitch.disabledBg
                                  }
                        }>
                        <RadioGroup
                            sx={
                                !isSmallerThanSmall
                                    ? { flexDirection: 'row' }
                                    : { flexDirection: 'column' }
                            }>
                            <Box
                                sx={
                                    !isSmallerThanSmall
                                        ? {
                                              width: '33%',
                                              borderRight: `0.125rem solid ${t.palette.globalSwitch.disabledBg}`,
                                              paddingLeft: '0.563rem'
                                          }
                                        : {
                                              borderBottom: `0.125rem solid ${t.palette.globalSwitch.disabledBg}`,
                                              paddingLeft: '0.75rem'
                                          }
                                }>
                                <FormControlLabel
                                    componentsProps={{ typography: { fontSize: '0.875rem' } }}
                                    label="Approve"
                                    checked={
                                        medicalReviewDeterminationValue ===
                                        MedicalReviewDeterminationConst.Approved
                                    }
                                    control={
                                        <Radio
                                            size="small"
                                            onChange={(_: unknown, checked) => {
                                                setMedicalReviewDeterminationValue(
                                                    MedicalReviewDeterminationConst.Approved
                                                );
                                                checked &&
                                                    onMedicalDeterminationChange(
                                                        MedicalReviewDeterminationConst.Approved
                                                    );
                                                setAnyChangeMade(true);
                                            }}
                                        />
                                    }
                                />
                            </Box>

                            <Box
                                sx={
                                    !isSmallerThanSmall
                                        ? {
                                              width: '33%',
                                              borderRight: `0.125rem solid ${t.palette.globalSwitch.disabledBg}`,
                                              paddingLeft: '0.563rem'
                                          }
                                        : {
                                              borderBottom: `0.125rem solid ${t.palette.globalSwitch.disabledBg}`,
                                              paddingLeft: '0.75rem'
                                          }
                                }>
                                <FormControlLabel
                                    componentsProps={{ typography: { fontSize: '0.875rem' } }}
                                    label="Partially Approved"
                                    checked={
                                        medicalReviewDeterminationValue ===
                                        MedicalReviewDeterminationConst.PartiallyApproved
                                    }
                                    control={
                                        <Radio
                                            size="small"
                                            onChange={(_: unknown, checked) => {
                                                setMedicalReviewDeterminationValue(
                                                    MedicalReviewDeterminationConst.PartiallyApproved
                                                );
                                                checked &&
                                                    onMedicalDeterminationChange(
                                                        MedicalReviewDeterminationConst.PartiallyApproved
                                                    );
                                                setAnyChangeMade(true);
                                            }}
                                        />
                                    }
                                />
                            </Box>
                            <Box
                                sx={
                                    !isSmallerThanSmall
                                        ? { width: '33%', paddingLeft: '0.563rem' }
                                        : { paddingLeft: '0.75rem' }
                                }>
                                <FormControlLabel
                                    componentsProps={{ typography: { fontSize: '0.875rem' } }}
                                    label="Denied"
                                    checked={
                                        medicalReviewDeterminationValue ===
                                        MedicalReviewDeterminationConst.Denied
                                    }
                                    control={
                                        <Radio
                                            size="small"
                                            onChange={(_: unknown, checked) => {
                                                setMedicalReviewDeterminationValue(
                                                    MedicalReviewDeterminationConst.Denied
                                                );
                                                checked &&
                                                    onMedicalDeterminationChange(
                                                        MedicalReviewDeterminationConst.Denied
                                                    );
                                                setAnyChangeMade(true);
                                            }}
                                        />
                                    }
                                />
                            </Box>
                        </RadioGroup>
                    </Box>
                ) : (
                    <></>
                )}
                <Box sx={medicalDeterminationRationaleRowStyle}>
                    <Typography sx={medicalDeterminationRationaleTextStyle}>
                        Briefly document your medical review findings and determination for each
                        approved and denied code
                    </Typography>
                    <TextareaAutosize
                        value={reasonMedicalReviewDeterminationValue}
                        minRows={2}
                        style={{
                            minHeight: '1.25rem'
                        }}
                        onChange={(v) => handleReasonMedicalReviewDetermination(v)}
                    />
                </Box>
            </Box>

            {(medicalReviewDeterminationValue === MedicalReviewDeterminationConst.Denied ||
                medicalReviewDeterminationValue ===
                    MedicalReviewDeterminationConst.PartiallyApproved) && (
                <Box sx={{ marginTop: '1rem', marginLeft: '0.5rem' }}>
                    <Typography variant="h5">Is this case appropriate for an APP P2P?</Typography>
                    <RadioGroup
                        row
                        value={isAppAppropriate !== null ? String(isAppAppropriate) : ''}
                        onChange={(e) => {
                            if (e.target.value !== 'true') {
                                setSelectedReason(null);
                                setReasonError(false);
                            }

                            setIsAppAppropriate(e.target.value === 'true');
                            setAnyChangeMade(true);
                        }}>
                        <FormControlLabel
                            value="true"
                            control={<Radio size="small" />}
                            label="Yes"
                        />
                        <FormControlLabel
                            value="false"
                            control={<Radio size="small" />}
                            label="No"
                        />
                    </RadioGroup>

                    {isAppAppropriate && (
                        <DecisionReasonSelectMedicalDetermination
                            value={selectedReason}
                            reasons={appReasonsList || []}
                            onChange={(v) => {
                                setSelectedReason(v);
                                setAnyChangeMade(true);
                                setReasonError(isAppAppropriate && !v);
                            }}
                            error={reasonError || !selectedReason}
                        />
                    )}
                </Box>
            )}
            <Dialog open={determinationAlertOpen} onClose={() => setDeterminationAlertOpen(false)}>
                <DialogContent>
                    {`Your determination does not align with the nurse's recommendation. 
                    Please provide a detailed explanation in the Medical Determination Rationale 
                    section below to clarify your reasoning`}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleAcknowledged}>Acknowledged</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PhysicianReviewSecondPart;
